import {Header} from 'semantic-ui-react';
import {orderBy as _orderBy} from 'lodash';
import PropTypes from 'prop-types';
import React, {useMemo, useEffect} from 'react';
import {useQuery} from '@apollo/client';

import ClientBookingsBulkChangeForm from '@modules/client/forms/ClientBookingsBulkChangeForm';
import ClientBookingsBulkChangeModel from '@modules/client/forms/ClientBookingsBulkChangeModel';
import {
    getCompetitors,
    getContentTierName,
    getInvoiceStatus,
    renderContentCategoryLevelColumn,
} from '@modules/client/utils/clientBookingTable';
import SelectedBookingsTable from '@modules/client/components/ClientBookingsBulkChange/SelectedBookingsTable';
import {
    showErrorDuringBulkChange,
    showErrorTooManyBookingsSelected,
} from '@modules/client/utils/bookingsBulkChange/errorModals';
import {convertToInt} from '@utils/helpers';
import {createForm} from '@utils/forms';
import {GetClientBookingsForBulkChange, GetClientPackagesForBulkChange} from '@graphql/clientBooking/query';
import {GetInvoiceStatuses} from '@graphql/invoiceStatus/query';
import responseStatus from '@constants/responseStatuses';

const createBookingForTable = (booking) => ({
    id: booking.id,
    start_datetime: booking.start_datetime,
    event_type: booking.event_type,
    match_id: booking.match_id,
    match_or_stage_id: booking.match_id,
    match_sport_name: booking.match_sport_name,
    match_tournament_category_name: booking.match_tournament_category_name,
    match_tournament_name: booking.match_tournament_name,
    sr_event_sport_name: booking.sr_event_sport_name,
    sr_event_tournament_category_name: booking.sr_event_tournament_category_name,
    sr_event_tournament_name: booking.sr_event_tournament_name,
    content_category_level_1_name: renderContentCategoryLevelColumn(booking, 'sport', 1),
    content_category_level_2_name: renderContentCategoryLevelColumn(booking, 'tournament_category', 2),
    content_category_level_3_name: renderContentCategoryLevelColumn(booking, 'tournament', 3),
    content_tier_name: getContentTierName(booking),
    competitors: getCompetitors(booking),
    event_content_name: booking.event_content_name,
    event_content_type_name: booking.event_content_type_name,
    event_content_variant_id: booking.event_content_variant_id,
    event_content_variant_name: booking.event_content_variant_name,
    event_content_variant_short_name: booking.event_content_variant_short_name,
    product_id: booking.product_id,
    product_short_name: booking.product_short_name,
    distribution_type_name: booking.distribution_type_name,
    client_package_name: booking.client_package_name,
    is_hq: booking.is_hq,
    is_ll: booking.is_ll,
    booking_type_name: booking.booking_type_name,
    price: booking.price,
    auto_update_price: booking.auto_update_price ? 1 : 0,
    booking_invoice_status_name: booking.booking_invoice_status_name,
    event_invoice_status_name: booking.event_invoice_status_name,
    product_is_invoiceable: booking.product_is_invoiceable,
    invoice_status: getInvoiceStatus(booking),
    auto_update_geo_restrictions: booking.auto_update_geo_restrictions ? 1 : 0,
    event_id: booking.event_id,
    event_description: booking.event_description,
    event_external_description: booking.event_external_description,
    event_color: booking.event_color,
    client_package_id: booking.client_package_id,
    is_cancelled: booking.is_cancelled,
});

const BookingBulkChangeModalContent = ({clientId, bookingsIds}) => {
    const {
        data: {
            invoiceStatuses = [],
        } = {},
        loading: invoiceStatusesLoading,
    } = useQuery(GetInvoiceStatuses, {
        fetchPolicy: 'network-only',
    });

    const {
        data: {
            clientBookings = [],
        } = {},
        error: clientBookingsError,
        loading: clientBookingsLoading,
    } = useQuery(GetClientBookingsForBulkChange, {
        variables: {
            client: clientId,
            bookings: bookingsIds,
        },
        fetchPolicy: 'network-only',
    });

    const sortedClientBookings = useMemo(() =>
        _orderBy(
            clientBookings.map(createBookingForTable),
            ['start_datetime'],
            ['desc']
        ),
    [clientBookings]
    );

    const {foundBookingsIds, productsIds, bookingDetails} = useMemo(() => ({
        foundBookingsIds: sortedClientBookings.map(b => b.id),
        productsIds: new Set(sortedClientBookings.map(b => b.product_id)),
        bookingDetails: sortedClientBookings.map(b => ({
            id: b.id,
            product: b.product_id,
            is_invoiceable: b.product_is_invoiceable,
        })),
    }), [sortedClientBookings]);

    const isCommonProduct = 1 === productsIds.size;
    const commonProductId = isCommonProduct ? [...productsIds].map(convertToInt) : null;

    const {
        data: {
            clientPackages = [],
        } = {},
        loading: clientPackagesLoading,
    } = useQuery(GetClientPackagesForBulkChange, {
        skip: !isCommonProduct,
        variables: {client: [clientId], commonProduct: commonProductId},
        fetchPolicy: 'network-only',
    });

    const loading = clientBookingsLoading || invoiceStatusesLoading || clientPackagesLoading;

    const Form = useMemo(() => createForm(
        () => ClientBookingsBulkChangeModel(isCommonProduct),
        ClientBookingsBulkChangeForm,
        {
            client: clientId,
            queryForRefetch: 'clientBookingsForTable',
            bookingDetails,
            isCommonProduct,
        }
    ), [bookingDetails, isCommonProduct, loading]);

    const hasBookingMismatch = !clientBookingsLoading && bookingsIds.length !== foundBookingsIds.length;
    const isUriTooLong = 0 < clientBookingsError?.graphQLErrors?.length
        && clientBookingsError.graphQLErrors[0].code === responseStatus.HTTP_URI_TOO_LONG;

    useEffect(() => {
        if (hasBookingMismatch) {
            if (isUriTooLong) {
                showErrorTooManyBookingsSelected();
            } else {
                showErrorDuringBulkChange();
            }
        }
    }, [hasBookingMismatch, isUriTooLong]);

    if (hasBookingMismatch) {
        return null;
    }

    return (
        <>
            <Header content='Modify bookings'/>
            <div className='ui form'>
                <SelectedBookingsTable
                    loading={clientBookingsLoading}
                    bookings={sortedClientBookings}
                    clientId={clientId}
                />
            </div>
            {!clientBookingsLoading && <div>
                <Form formData={{variables: {id: bookingsIds}}}
                    clientPackages={clientPackages}
                    invoiceStatuses={invoiceStatuses}
                    loading={clientPackagesLoading || invoiceStatusesLoading}/>
            </div>}
        </>
    );
};

BookingBulkChangeModalContent.propTypes = {
    bookingsIds: PropTypes.array.isRequired,
    clientId: PropTypes.number.isRequired,
};

export default BookingBulkChangeModalContent;
